import * as React from 'react';
// @ts-expect-error
import {components} from 'react-select';
// @ts-expect-error
import type {SingleValueProps, OptionProps} from 'react-select';

import classnames from 'classnames';

import type {Assignable} from 'nutshell-core/types';

import {Avatar} from '../avatar/avatar';
// @ts-expect-error - imported from .js/.jsx file
import {ContactAssigneeIcon} from '../icon/svg/contact-assignee-icon';
import {SelectPickerGeneric} from '../select-picker-generic/select-picker-generic';

import styles from './assignee-select.module.css';

export type CustomOption = {
    id: string;
    name: string;
};

type Props = {
    inputId?: string;
    options: ReadonlyArray<Assignable>;
    customOptions?: CustomOption[];
    value: string | null | undefined; // ID,
    onChange: (id?: string | null | undefined) => void;
    isClearable?: boolean;
    placeholder?: string;
    isDisabled?: boolean;
    withControlIcon?: boolean;
    hideDividerWhenEmpty?: boolean;
    minHeight?: number;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    menuPortalTarget?: HTMLElement;
    showTooltip?: boolean;
};

type Option = {
    value: Assignable | CustomOption;
    label: string;
};

type AssigneeSingleValueProps = SingleValueProps & {
    showTooltip?: boolean;
};

/**
 * Use this to render a dropdown to choose a single assignee.
 */
export const AssigneeSelect = (props: Props) => {
    const {
        inputId,
        options,
        customOptions,
        value,
        onChange,
        isClearable = true,
        placeholder = 'Choose an assignee…',
        isDisabled,
        withControlIcon,
        hideDividerWhenEmpty,
        minHeight,
        menuPlacement = 'auto',
        menuPortalTarget,
        showTooltip,
    } = props;

    let optionsList: Option[] = options.map((item) => ({
        value: item,
        label: item.name,
        isDisabled: item.isEnabled === false,
        isLoggedInUser: item.isLoggedInUser,
    }));

    if (customOptions) {
        optionsList = customOptions
            .map((item) => ({
                value: item,
                label: item.name,
            }))
            .concat(optionsList);
    }

    const selectedValue = optionsList.find((opt) => opt.value.id === value);

    let controlIcon;
    if (withControlIcon) {
        // When selected value is a user, we show the avatar - so no need for another icon
        // @ts-expect-error
        if (!selectedValue || (selectedValue && selectedValue.value.type === 'teams')) {
            controlIcon = ContactAssigneeIcon;
        }
    }

    const handleChange = (newOption: Option) => {
        onChange(newOption.value.id);
    };

    const handleClear = () => {
        onChange(null);
    };

    const isOptionSelected = (option: Option, selectedValues: Option[]) => {
        // @ts-expect-error
        return option.value === selectedValues[0];
    };

    return (
        <SelectPickerGeneric
            inputId={inputId}
            backspaceRemovesValue={false}
            openMenuOnFocus={true}
            menuShouldScrollIntoView={true}
            value={selectedValue ? {...selectedValue.value, showTooltip: showTooltip} : null}
            onChange={handleChange}
            onClear={isClearable ? handleClear : undefined}
            options={optionsList}
            isDisabled={isDisabled}
            blurInputOnSelect={true}
            isOptionSelected={isOptionSelected}
            placeholder={placeholder}
            styles={{
                // @ts-expect-error
                menu: (base) => ({...base, zIndex: 5}),
            }}
            components={{
                Option: OptionComponent,
                SingleValue: SingleValueComponent,
            }}
            controlIcon={controlIcon}
            hideDividerWhenEmpty={hideDividerWhenEmpty}
            minHeight={minHeight}
            menuPlacement={menuPlacement}
            menuPortalTarget={menuPortalTarget}
        />
    );
};

const SingleValueComponent = (props: AssigneeSingleValueProps) => {
    return (
        <components.SingleValue {...props}>
            <AssignableComponent assignable={props.data} showTooltip={props.data.showTooltip} />
        </components.SingleValue>
    );
};

const OptionComponent = (props: OptionProps) => {
    return (
        <components.Option {...props}>
            <AssignableComponent
                assignable={props.data.value}
                isLoggedInUser={props.data.isLoggedInUser}
            />
        </components.Option>
    );
};

type AssignableComponentProps = {
    assignable: Assignable | CustomOption;
    isLoggedInUser?: boolean;
    showTooltip?: boolean;
};

const AssignableComponent = ({
    assignable,
    isLoggedInUser,
    showTooltip,
}: AssignableComponentProps) => (
    <div
        data-tip={showTooltip ? assignable.name : undefined}
        className={styles['option-container']}
    >
        {/* @ts-expect-error */}
        {assignable.type && assignable.type === 'users' ? (
            <Avatar
                // @ts-expect-error
                avatarUrl={assignable.avatarUrl}
                // @ts-expect-error
                initials={assignable.initials}
                size={20}
                // @ts-expect-error
                type={assignable.type}
                // @ts-expect-error
                isDisabled={!assignable.isEnabled}
            />
        ) : null}
        <div
            className={classnames(
                // @ts-expect-error
                assignable.type === 'users' && !assignable.isEnabled
                    ? 'light-text strikethrough'
                    : undefined,
                styles['option-title']
            )}
        >
            {assignable.name}
            {isLoggedInUser && <span className={styles['you-text']}> (You)</span>}
        </div>
    </div>
);
